import { fetchProductDetails } from 'api/product';
import { getPersonalizedProductDetails } from 'api/product/getPersonalizedProductDetails';
import { CrossSellProductDetails } from 'interfaces/product';

/**
 * Check if it is possible to activate a subscription for the product
 */
export const isSubscriptionEnabled = (
  productTags: Array<string> | string,
  productId: number
): boolean => {
  typeof productTags === 'string' &&
    (productTags = productTags.split(',').map((tag) => tag.trim()));
  // this array contains the IDs of the products for which the subscription is disabled no matter what, even if the products contain the tag 'SubscriptionEnabled'.
  // This may be due to marketing decisions.
  // e.g. see: https://petsdeli.atlassian.net/browse/PT-3934
  const PRODUCTS_TO_DISABLE_SUBSCRIPTION = [7996787294474, 7996772843786];
  if (PRODUCTS_TO_DISABLE_SUBSCRIPTION.includes(productId)) return false;
  return (productTags || '').includes('SubscriptionEnabled');
};

/**
 * Returns the price minus the selected percentage
 * @param {number } value - The original product price
 * @param {number} percentage - The percentage to be subtracted
 * @returns {number} - The price minus the percentage
 */
export const minusPercent = (value: number, percentage: number): number => {
  return Number((value - value * (percentage / 100)).toFixed(2));
};

/**
 * Format the title of the product
 */
export const formatTitle = (title: string): string => title.replace(/\s\(\dweek\)/, '');

/**
 * Fetch product details and return the data in the format of the upsell product
 */
export const createCrossSellProductDetails = async (
  handle: string,
  variantId: string,
  ruleId?: string
): Promise<CrossSellProductDetails | null> => {
  const productDetails = await fetchProductDetails(handle);

  const targetVariant = productDetails?.product.variants.find(
    (variant) => variant.id === variantId
  );

  if (!productDetails || !targetVariant) return null;

  const personalizedProductDetails = await getPersonalizedProductDetails({
    product_id: productDetails?.product.id,
    ...(ruleId != null && { rule_id: ruleId }),
  });

  const targetPersonalizedVariantDetails = personalizedProductDetails?.variants?.find(
    (variant) => variant.id === variantId
  );

  // price with savings plan discount applied
  const currentPrice = targetPersonalizedVariantDetails?.one_off.price;
  // original price as it was before any discount being applied
  const originalPrice = targetPersonalizedVariantDetails?.one_off.compare?.before;

  if (!targetPersonalizedVariantDetails) return null;

  return {
    availableForSale: targetVariant.availableForSale,
    handle: productDetails?.product.handle,
    price: Number(currentPrice),
    sku: targetVariant?.sku,
    variantId: variantId,
    productId: productDetails?.product.id,
    productTitle: productDetails?.product.title,
    productType: productDetails?.product.productType,
    variantTitle: targetVariant?.title,
    tags: productDetails?.product.tags.toString(),
    quantity: 1,
    image: { src: targetVariant.image.originalSrc },
    ...(originalPrice && {
      compareAtPrice: Number(originalPrice),
    }),
  };
};
